import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import {
  makeStyles,
  Link,
  Typography,
  Button,
  IconButton,
  ClickAwayListener,
  Menu,
  MenuItem,
  Icons,
} from '~/lib/bn-material-ui';
import { useSirenEntity, muiTheme, useFullSirenCollection } from '~/utility/BinSentry-ui-utility';
import { useOrganizationAncestors } from '~/organizations/organization-ancestors';
import { Entity } from 'siren-parser';
import { OrganizationTypeEntity, OrganizationType, ALL_ORGANIZATION_TYPES } from '~/types/organization-type-entity';
import { OrganizationEntity } from '~/types/organization-entity';

const usePathNameStyles = makeStyles<typeof muiTheme>(({ breakpoints, palette }) => {
  return {
    pathContainer: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    pathItem: {
      display: 'flex',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      '& p': {
        maxWidth: '20rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [breakpoints.down('sm')]: {
          maxWidth: '7rem',
        },
      },
    },
    chevron: {
      paddingLeft: '0.5rem',
      paddingRight: '0.5rem',
      color: palette.blue[50],
      fontWeight: 900,
    },
    link: {
      padding: 0,
      minWidth: 'unset',
      cursor: 'pointer',
    },
    siblingDropdownButton: {
      padding: 0,
      minWidth: 'unset',
    },
  };
});

type PathNameDropdownType = 'sibling' | 'children';
type PathNameClass = 'pathItem' | 'chevron' | 'link' | 'pathContainer';
export type OrganizationPathNameLinks = Partial<Record<OrganizationType, string>>;
export type PathNameClasses = Partial<Record<PathNameClass, string>>;

function getOrganizationLink(organizationEntity?: Entity) {
  const organizationHref = organizationEntity?.getLinkByRel('self')?.href;
  return organizationHref ? `/customers?organizationId=${encodeURIComponent(organizationHref)}` : undefined;
}

/**
 * @param {string} organizationHref
 * @param {OrganizationType} endOrganizationType
 * @param {boolean} openNewWindow link opens in new tab/window when clicked
 * @param {OrganizationPathNameLinks} organizationTypeLinks object with organization type as key and custom link as value. Defaults provided
 * @param {OrganizationType[]} showOrganizationTypeLinks array of organization types to show as links. Defaults to all
 * @param {function} handleDropdownClick when defined shows dropdown on final path item; dependant on dropdownType
 * @param {PathNameDropdownType} dropdownType defines which type of dropdown to display
 * @param {PathNameClasses} classes object with classnames to override defaults
 */
export function OrganizationPathName({
  organizationHref,
  endOrganizationType = 'account',
  openNewWindow = false,
  excludeBinsentry = true,
  organizationTypeLinks = {},
  showOrganizationTypeLinks = ALL_ORGANIZATION_TYPES,
  handleDropdownClick,
  dropdownType,
  classes,
  children,
}: {
  organizationHref?: string;
  endOrganizationType?: OrganizationType;
  openNewWindow?: boolean;
  excludeBinsentry?: boolean;
  organizationTypeLinks?: OrganizationPathNameLinks;
  showOrganizationTypeLinks?: OrganizationType[];
  handleDropdownClick?: (organization: OrganizationEntity) => void;
  dropdownType?: PathNameDropdownType;
  classes?: PathNameClasses;
  children?: React.ReactNode;
}) {
  const styles = usePathNameStyles();
  const { entity: organizationEntity, loading: loadingOrganization } = useSirenEntity({ href: organizationHref });
  const organizationTypeEntity = organizationEntity?.getSubEntity('https://api.binsentry.com/rel/organization-type') as OrganizationTypeEntity;

  const { ancestors } = useOrganizationAncestors({
    startType: organizationTypeEntity?.properties?.name,
    endType: endOrganizationType,
    excludeBinsentry,
    href: organizationHref,
  }) as { ancestors: OrganizationEntity[] };

  if (loadingOrganization) {
    return null;
  }

  return <div className={classNames(styles.pathContainer, classes?.pathContainer)}>
    {ancestors?.map((ancestor: OrganizationEntity, i: number) => {
      if (!ancestor) {
        return null;
      }
      const isLastPathItem = i === ancestors.length - 1;
      const currentPathItem = <PathItem
        showOrganizationTypeLinks={showOrganizationTypeLinks}
        organizationTypeLinks={organizationTypeLinks}
        openNewWindow={openNewWindow}
        organization={ancestor}
        isLastPathItem={isLastPathItem}
        classes={classes}
      />;

      if (isLastPathItem && handleDropdownClick && dropdownType === 'sibling') {
        return <OrganizationDropdownButton
          key={i}
          organization={ancestor}
          handleDropdownClick={handleDropdownClick}
          dropdownType={dropdownType}
        >
          {currentPathItem}
        </OrganizationDropdownButton>;
      }

      return <div className={classNames(styles.pathItem, classes?.pathItem)} key={i}>
        {currentPathItem}
      </div>;
    })}
    {handleDropdownClick && dropdownType === 'children'
      ? <OrganizationDropdownButton
        organization={(_.last(ancestors) || organizationEntity) as OrganizationEntity}
        handleDropdownClick={handleDropdownClick}
        dropdownType={dropdownType}
        classes={classes}
      >{children}</OrganizationDropdownButton>
      : children
    }
  </div>;
}

export function PathNameChevron({ className }: { className?: string }) {
  const styles = usePathNameStyles();
  return <div className={classNames(styles.chevron, className)}>{'>'}</div>;
}

function PathItem ({
  showOrganizationTypeLinks,
  organizationTypeLinks,
  openNewWindow,
  organization,
  isLastPathItem,
  classes,
}: {
  showOrganizationTypeLinks: OrganizationType[];
  organizationTypeLinks: OrganizationPathNameLinks;
  openNewWindow: boolean;
  organization: OrganizationEntity;
  isLastPathItem: boolean;
  classes?: PathNameClasses;
}) {
  const styles = usePathNameStyles();
  const ancestorOrganizationTypeEntity = organization.getSubEntityByRel('https://api.binsentry.com/rel/organization-type') as OrganizationTypeEntity;
  const ancestorOrganizationType = ancestorOrganizationTypeEntity.properties.name as OrganizationType;
  const href = organizationTypeLinks[ancestorOrganizationType] || getOrganizationLink(organization);

  return <>
    {showOrganizationTypeLinks.includes(ancestorOrganizationType) && href
      ? <Link
        href={href}
        {...openNewWindow ? { target: '_blank' } : {}}
        className={classNames(styles.link, classes?.link)}
      >
        <Typography>{organization.properties.name}</Typography>
      </Link>
      : <Typography>{organization.properties.name}</Typography>
    }
    {!isLastPathItem && <PathNameChevron className={classes?.chevron} />}
  </>;
}

function useDropdownOrganizations(dropdownType: PathNameDropdownType, organization?: OrganizationEntity) {
  const parentOrgHref = dropdownType === 'children'
    ? organization?.getLink('self')?.href
    : organization?.getSubEntityByRel('https://api.binsentry.com/rel/organization-parent')?.href;
  const { entity: parentOrganization } = useSirenEntity({ href: parentOrgHref });
  const childrenEntityHref = parentOrganization?.getSubEntityByRel('https://api.binsentry.com/rel/v2/organization-children')?.href;
  const { collection: dropdownOrgEntities } = useFullSirenCollection({ href: childrenEntityHref, onlyLoadFirstPage: false });
  return dropdownOrgEntities.filter((dropdownOrg: OrganizationEntity ) => dropdownOrg.getLink('self')?.href !== organization?.getLink('self')?.href);
}

function OrganizationDropdownButton({
  organization,
  handleDropdownClick,
  dropdownType,
  children,
  classes,
}: {
  organization?: OrganizationEntity;
  handleDropdownClick: (organization: OrganizationEntity) => void;
  dropdownType: PathNameDropdownType;
  children: React.ReactNode;
  classes?: PathNameClasses;
}) {
  const styles = usePathNameStyles();
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const dropdownOrganizations = useDropdownOrganizations(dropdownType, organization);

  if (dropdownOrganizations?.length === 0) {
    return <>{children}</>; // When no organizations in dropdown, make sure the PathItem renders as defined in OrganizationPathName
  }

  return <div>
    {dropdownType === 'sibling' &&
      <Button
        ref={anchorRef}
        color='secondary'
        onClick={handleToggle}
        className={styles.siblingDropdownButton}
      >
        <Typography style={{ textDecoration: 'none' }}>{organization?.properties.name}</Typography>
        <Icons.ArrowDropDown />
      </Button>
    }
    {dropdownType === 'children' &&
      <div className={classNames(styles.pathItem, classes?.pathItem)}>
        <PathNameChevron className={classes?.chevron} />
        <IconButton
          ref={anchorRef}
          color='secondary'
          onClick={handleToggle}
          className={styles.siblingDropdownButton}
        >
          <Icons.MoreHoriz />
        </IconButton>
      </div>
    }
    <ClickAwayListener onClickAway={handleClose}>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleToggle}
      >
        {dropdownOrganizations?.map((organization: OrganizationEntity, index: number) => {
          return <MenuItem key={index} onClick={() => {
            setOpen(false);
            handleDropdownClick(organization);
          }}>
            <Typography>{organization.properties.name}</Typography>
          </MenuItem>;
        })}
      </Menu>
    </ClickAwayListener>
  </div>;
}
